<script lang="ts">
  import type {ITag} from "$lib/interfaces/ITag";
  import ArrowRightIcon from "$lib/icons/ArrowRightIcon.svelte";
  import {createEventDispatcher, onMount} from "svelte";
  import {environment} from "$lib/env";
  import {translate} from "$lib/i18n";
  import {clickOutside} from "$lib/directives/clickOutside";

  let tags: ITag[] = [];
  let selected: ITag | null = $state(null);
  let search: string = "";
  let searchResults: ITag[] = $state([]);
  let isOpen = $state(false);
  let dropdownRef: HTMLDivElement;
  interface Props {
    gameCategorySlug: string;
    id: string;
    css?: string;
  }

  let { gameCategorySlug, id, css = 'bg-primary-dark -rota rounded-full py-4 transition-all px-8 flex items-center' }: Props = $props();

  // Track previous slug to detect changes
  let previousSlug = gameCategorySlug;

  // Watch for changes to gameCategorySlug
  $effect(() => {
    if (gameCategorySlug !== previousSlug) {
      previousSlug = gameCategorySlug;
      // Reset selection and fetch new tags when category changes
      selected = {id: '', name: 'All', slug: 'all'};
      promise = getTags();
    }
  });

  let promise = getTags();
  const dispatch = createEventDispatcher();

  function toggleDropdown() {
    isOpen = !isOpen;
    if (!isOpen) {
      search = '';
      searchResults = tags;
    }
  }

  async function getTags() {
    try {
      const response = await fetch(`${environment.apiUrl}/game-categories/${gameCategorySlug}/tags`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (data && data.data) {
        tags = [...data.data];
        searchResults = data.data;

        // Always reset to "All" when fetching new tags
        selected = {id: '', name: 'All', gameCount: 0, slug: '', imageUrl: ''};

        // Notify parent that selection has been reset
        dispatch('selected', selected);

        return data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching tags:", error);
      return [];
    }
  }

  function searchTag(value: string) {
    searchResults = tags.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()));
  }

  function selectProvider(tag: ITag) {
    selected = tag;
    dispatch('selected', tag);
    toggleDropdown();
  }

  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.currentTarget as Node)) {
      isOpen = false;
    }
  }

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });
</script>

<div class="relative" use:clickOutside={() => isOpen = false}>
  <button class="{css}" {id} name={id}
          onclick={() => toggleDropdown()}>
    {#if !selected || selected.id === ''}
      <span class="text-white mr-2">{$translate('Tag')}:</span>
    {/if}
    {#if selected && selected.id !== ''}
      <span class="text-primary-fire text-nowrap">{selected.name}</span>
    {:else}
      <span class="text-primary-fire">{$translate('All')}</span>
    {/if}
    <ArrowRightIcon css="ml-2 {!isOpen ? 'rotate-90' : '-rotate-90'}"/>
  </button>
  <div class="{isOpen ? 'absolute': 'hidden'} top-16 animate-fadeIn z-50 right-0 w-64 bg-primary-dark p-2 rounded-xl shadow-xl drop-shadow-lg border border-primary-lightBlue/20">
    <input type="text" placeholder="Search" oninput={(e) => searchTag(e.currentTarget.value)}
           class="w-full p-2 bg-primary-dark outline-0 border-b border-primary-lightBlue text-white" autofocus />
    <div class="max-h-60 overflow-y-auto flex flex-col gap-2 pt-1">
      {#await promise}
        <div class="min-h-[60px] flex justify-center items-center col-span-full">
          <enhanced:img src="$lib/logo.svg?enhanced" class="animate-bounce" width="46" alt="Forza.bet"/>
        </div>
      {:then tagsLoaded}
        <div class="px-3 py-2.5 hover:bg-primary-blue cursor-pointer rounded-lg {selected?.id === '' ? 'text-primary-fire bg-primary-blue/40' :'text-white'}" onclick={() => selectProvider({id: '', name: 'All', slug: ''})}>
          All
        </div>
        {#each searchResults as tag}
          <div class="px-3 py-2.5 hover:bg-primary-blue cursor-pointer rounded-lg {selected?.id === tag.id ? 'text-primary-fire bg-primary-blue/40' :'text-white'}" onclick={() => selectProvider(tag)}>
            {tag.name}
          </div>
        {/each}
      {:catch error}
        <p class="text-primary-fire">{error.message}</p>
      {/await}
    </div>
  </div>
</div>

